import { cn } from "@/lib/utils";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";

// ────────────────────────────────────────────────────────────────────────────
// The zero-inbox wall — what Jeff sees at /email/board.
//
// Reads the canon render payload (one file, written by the script-only ingest
// from the mirror) and renders it. Every string Jeff sees is LOCKED to the
// wording table in GOALS/email-zero-inbox/PROJECT.md:
//
//   needs reply        → "Needs a reply"                  (red row)
//   call-backs owed    → "Call-backs owed"                 (red row)
//   burst              → "Caught a burst — not counted as open" (amber line)
//   30–90 days open    → "Still open — waiting longer"     (strip beside the headline)
//   91+ days open      → "Waiting — old"                   (red strip)
//   archived           → "Left parked — archive candidate" (neutral line)
//   unfiled brand      → "Not sorted yet — catching every pass"
//   nothing at all     → "Inbox zero — nothing waiting on you" (green, healthy only)
//   nothing new        → "Nothing new waiting on you"
//   feed behind        → "Count from <weekday> <date> <time> — feed behind"
//   feed stale         → "board stale — not checking right now"
//
// No file paths, no error codes, no row ids, no addresses. The component is
// pure: it takes the payload as a prop so it can be rendered and checked
// without a browser.
// ────────────────────────────────────────────────────────────────────────────

const STALE_AMBER_HOURS = 30;   // amber past this
const STALE_RED_HOURS = 48;     // red past this

const L_NEEDS_REPLY = "Needs a reply";
const L_CALL_BACKS = "Call-backs owed";
const L_BURST = "Caught a burst — not counted as open";
const L_WAITING_LONGER = "Still open — waiting longer";
const L_WAITING_OLD = "Waiting — old";
const L_ARCHIVE = "Left parked — archive candidate";
const L_UNFILED = "Not sorted yet — catching every pass";
const L_INBOX_ZERO = "Inbox zero — nothing waiting on you";
const L_NOTHING_NEW = "Nothing new waiting on you";
const L_BEHIND = "— feed behind";
const L_STALE = "board stale — not checking right now";

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

// Days the stamp has been sitting still (the mirror's own ingest stamp).
const ageHours = (asOfIso) => {
  if (!asOfIso) return Infinity;
  const t = Date.parse(asOfIso);
  if (Number.isNaN(t)) return Infinity;
  return (Date.now() - t) / 36e5;
};

// The stamp Jeff reads: weekday + date + time, plus the health of the feed.
//   fresh  → "Count from Wed Oct 21 7:49 PM"
//   amber  → "Count from Wed Oct 21 7:49 PM — feed behind"  + "board is behind — checking twice a day"
//   red    → "board stale — not checking right now"          + "Count from …"
const Stamp = ({ asOfDisplay, hours }) => {
  const behind = hours > STALE_AMBER_HOURS;
  const stale = hours > STALE_RED_HOURS;
  return (
    <div className="flex flex-col items-end gap-1">
      {stale && (
        <p data-testid="zero-wall-stale" className="text-[0.8125rem] text-os-red">
          board stale — not checking right now
        </p>
      )}
      {!stale && behind && (
        <p data-testid="zero-wall-behind" className="text-[0.8125rem] text-os-amber">
          board is behind — checking twice a day
        </p>
      )}
      <MonoMeta
        as="p"
        data-testid="zero-wall-stamp"
        data-behind={behind ? "yes" : "no"}
        data-stale={stale ? "yes" : "no"}
        className={cn("shrink-0 whitespace-nowrap pt-1", stale ? "text-os-red" : behind ? "text-os-amber" : "text-os-ink-3")}
      >
        {`Count from ${asOfDisplay}${behind ? ` ${L_BEHIND}` : ""}`}
      </MonoMeta>
    </div>
  );
};

// Tier one: the count Jeff actually acts on.
const Headline = ({ clean, label, state }) => {
  if (state === "inbox-zero") {
    return (
      <p data-testid="zero-wall-headline-word" className="text-[1.375rem] font-semibold leading-tight text-os-green">
        {L_INBOX_ZERO}
      </p>
    );
  }
  return (
    <div className="flex items-baseline gap-3">
      <p data-testid="zero-wall-headline-count" className="text-[2.875rem] font-semibold leading-none tracking-[-0.035em] text-os-ink tabular-nums">
        {clean}
      </p>
      <p data-testid="zero-wall-headline-label" className="text-[0.9375rem] text-os-ink-2">
        {label}
      </p>
    </div>
  );
};

// The two red rows under the headline: what needs a reply, what owes a call-back.
// The labels are the LOCKED wording-table strings; the number leads.
const WorkRows = ({ needsReply, callBacks }) => {
  if (!needsReply && !callBacks) return null;
  return (
    <div data-testid="zero-wall-work-rows" className="mt-3 flex flex-col gap-1">
      {needsReply > 0 && (
        <p data-testid="zero-wall-row-needs-reply" className="text-[0.9375rem] text-os-red">
          <span className="font-semibold tabular-nums">{needsReply}</span> — {L_NEEDS_REPLY}
        </p>
      )}
      {callBacks > 0 && (
        <p data-testid="zero-wall-row-call-backs" className="text-[0.9375rem] text-os-red">
          <span className="font-semibold tabular-nums">{callBacks}</span> — {L_CALL_BACKS}
        </p>
      )}
    </div>
  );
};

// The strip that sits right beside the headline: open, but older than 30 days.
const WaitingLongerStrip = ({ count }) => {
  if (!count) return null;
  return (
    <Card quiet data-testid="zero-wall-waiting-longer" className="px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <StateWord tone="amber">{L_WAITING_LONGER}</StateWord>
        <span className="text-[1.25rem] font-semibold tabular-nums text-os-ink">{count}</span>
        <span className="text-[0.875rem] text-os-ink-2">
          {count === 1 ? "message is open and older than 30 days" : "messages are open and older than 30 days"}
        </span>
      </div>
    </Card>
  );
};

// Open for 91 days or more and still unanswered — red, never hidden.
const WaitingOldStrip = ({ count }) => {
  if (!count) return null;
  return (
    <Card quiet data-testid="zero-wall-waiting-old" className="border-os-red px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <StateWord tone="red">{L_WAITING_OLD}</StateWord>
        <span className="text-[1.25rem] font-semibold tabular-nums text-os-red">{count}</span>
        <span className="text-[0.875rem] text-os-ink-2">
          {count === 1 ? "message is open for more than 90 days" : "messages are open for more than 90 days"}
        </span>
      </div>
    </Card>
  );
};

// The burst line: it reports, it never hides. The number is exactly the rows
// the filter took out of the open count.
const BurstLine = ({ burst }) => {
  const messages = num(burst?.messages);
  if (!messages && !num(burst?.count)) return null;
  return (
    <Card quiet data-testid="zero-wall-burst" className="px-5 py-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <StateWord tone="amber">{L_BURST}</StateWord>
        <span className="text-[0.875rem] text-os-ink-2">{burst?.note}</span>
      </div>
    </Card>
  );
};

// One brand tile. The unfiled bucket is rendered by the same tile, never hidden.
const BrandTile = ({ tile, count, needsReply, callBacks, waitingLonger, waitingOld, isUnfiled }) => {
  const quiet = !count && !waitingLonger && !waitingOld;
  return (
    <Card
      quiet={quiet}
      data-testid={isUnfiled ? "zero-wall-tile-unfiled" : "zero-wall-tile"}
      data-brand={tile}
      className="px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-[0.9375rem] font-semibold text-os-ink">{tile}</p>
        <p className="text-[1.75rem] font-semibold leading-none tabular-nums text-os-ink">{count}</p>
      </div>
      <div className="mt-2 flex flex-col gap-0.5">
        {needsReply > 0 && (
          <p data-testid="zero-wall-tile-needs-reply" className="text-[0.8125rem] text-os-red">
            <span className="font-semibold tabular-nums">{needsReply}</span> — {L_NEEDS_REPLY}
          </p>
        )}
        {callBacks > 0 && (
          <p data-testid="zero-wall-tile-call-backs" className="text-[0.8125rem] text-os-red">
            <span className="font-semibold tabular-nums">{callBacks}</span> — {L_CALL_BACKS}
          </p>
        )}
        {waitingLonger > 0 && (
          <p data-testid="zero-wall-tile-waiting-longer" className="text-[0.8125rem] text-os-amber">
            <span className="font-semibold tabular-nums">{waitingLonger}</span> — {L_WAITING_LONGER}
          </p>
        )}
        {waitingOld > 0 && (
          <p data-testid="zero-wall-tile-waiting-old" className="text-[0.8125rem] text-os-red">
            <span className="font-semibold tabular-nums">{waitingOld}</span> — {L_WAITING_OLD}
          </p>
        )}
        {quiet && <p className="text-[0.8125rem] text-os-ink-3">Nothing waiting on you</p>}
      </div>
    </Card>
  );
};

export default function ZeroWall({ payload }) {
  const p = payload || {};
  const tiles = Array.isArray(p.tiles) ? p.tiles : [];
  const unfiled = p.unfiled || {};
  const clean = num(p.clean_count);
  const needsReply = num(p.headline?.needs_reply);
  const callBacks = num(p.headline?.call_backs);
  const longer = num(p.waiting_longer?.count);
  const old = num(p.waiting_old?.count);
  const hours = ageHours(p.as_of);

  // A board with no readable source is BROKEN, not empty. The healthy state
  // ("inbox zero") is only ever allowed when a real source was read.
  const hasSource = Boolean(p.as_of) && typeof p.clean_count === "number";

  // State comes from the payload, but the wall never trusts it blindly: the
  // healthy state ("inbox zero") is only allowed when nothing at all is open.
  const derivedState =
    !hasSource ? "broken" : clean > 0 ? "waiting" : longer > 0 || old > 0 ? "attention" : "inbox-zero";
  const state = derivedState;

  // The tiles must add up to the headline. If they don't, the board says so
  // out loud — a wrong number never renders as a quiet, healthy board.
  const tileSum = tiles.reduce((s, t) => s + num(t.clean), 0) + num(unfiled.clean);
  const reconciled = tileSum === clean;

  const headlineLabel =
    clean > 0
      ? p.headline?.label || `${needsReply} need a reply`
      : longer > 0 || old > 0
        ? L_NOTHING_NEW
        : L_INBOX_ZERO;

  // The broken state replaces the whole board with one loud line. A source that
  // can't be read is never shown as an empty inbox.
  if (!hasSource) {
    return (
      <div data-testid="zero-wall" data-state="broken" className="w-full font-inter text-os-ink">
        <Card quiet data-testid="zero-wall-broken" className="border-os-red px-5 py-4">
          <p className="text-[1.125rem] font-medium text-os-red">
            Kit can&apos;t read the mail feed right now — this board is broken, not empty.
          </p>
          <p className="mt-1 text-[0.875rem] text-os-ink-2">
            The numbers below are missing on purpose. Kit is looking at the feed.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="zero-wall" data-state={state} data-clean={clean} className="w-full font-inter text-os-ink">
      <div className="flex flex-col gap-3">
        {/* ── tier one: the count + the stamp ── */}
        <Card quiet data-testid="zero-wall-headline" className="px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <Label as="div">Waiting on you</Label>
              <div className="mt-2">
                <Headline clean={clean} label={headlineLabel} state={state} />
              </div>
              {clean > 0 && <WorkRows needsReply={needsReply} callBacks={callBacks} />}
              {clean === 0 && (longer > 0 || old > 0) && (
                <p data-testid="zero-wall-nothing-new" className="mt-2 text-[0.9375rem] text-os-red">
                  {`${longer + old} ${longer + old === 1 ? "is" : "are"} still open and older than 30 days`}
                </p>
              )}
            </div>
            <Stamp asOfDisplay={p.as_of_display || ""} hours={hours} />
          </div>
        </Card>

        {/* ── the strip beside the headline (never inside the count) ── */}
        <WaitingLongerStrip count={longer} />
        <WaitingOldStrip count={old} />

        {/* ── the burst line: reports, never hides ── */}
        <BurstLine burst={p.burst_line} />

        {/* ── brand tiles + the not-sorted-yet tile ── */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {tiles.map((t) => (
            <BrandTile
              key={t.brand || t.tile}
              tile={t.tile}
              count={num(t.clean)}
              needsReply={num(t.needs_reply)}
              callBacks={num(t.call_backs)}
              waitingLonger={num(t.waiting_longer)}
              waitingOld={num(t.waiting_old)}
            />
          ))}
          <BrandTile
            isUnfiled
            tile={unfiled.tile || L_UNFILED}
            count={num(unfiled.clean)}
            needsReply={num(unfiled.needs_reply)}
            callBacks={num(unfiled.call_backs)}
            waitingLonger={num(unfiled.waiting_longer)}
            waitingOld={num(unfiled.waiting_old)}
          />
        </div>

        {/* ── the archive pool count (answered or undeliverable, long parked) ── */}
        <Card quiet data-testid="zero-wall-archive" className="px-5 py-3">
          <div className="flex flex-wrap items-baseline gap-2">
            <StateWord tone="muted">{L_ARCHIVE}</StateWord>
            <span className="text-[1.25rem] font-semibold tabular-nums text-os-ink-2">
              {num(p.archive_pool?.count)}
            </span>
            <span className="text-[0.875rem] text-os-ink-3">
              {num(p.archive_pool?.count) === 1 ? "message is parked" : "messages are parked"}
            </span>
          </div>
        </Card>

        {/* ── the numbers must add up; if they don't, say it loudly ── */}
        {!reconciled && (
          <Card quiet data-testid="zero-wall-reconcile-error" className="border-os-red px-5 py-3">
            <p className="text-[0.875rem] text-os-red">
              The tiles on this board don&apos;t add up to the number above — Kit is looking at the feed.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}